/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Browser } from "@capacitor/browser";
import CoverCardMVP from "../../components/shared/CoverCardMVP";
import CoverCardNewVisual from "../../components/shared/CoverCardNewVisual";
import {
	Link,
	useLoaderData,
	useNavigate,
	useParams,
	useRouter,
} from "@tanstack/react-router";
import { Button } from "../../components/ui/button";
import "./../../styles/embeddedHtmlStyles.scss";
import {
	Drawer,
	DrawerContentWithoutCancelButton,
} from "../../components/ui/dropdownMobile";
import websiteIllustration from "../../assets/website_Illustration.png";
import { useReducer, useState } from "react";
import axios from "axios";
import { Skeleton } from "../../components/ui/skeleton";
import type { StudyStatus } from "../../routes/(app)/_app.study.$studyId";
import { AuthorsSimplyList } from "../../components/shared/AuthorsSimplyList";
import { Toast } from "@capacitor/toast";
import { LoaderSpinner } from "@/components/ui/LoaderSpinner";
import { chooseCoverDate } from "@/utils/dateAndTime";
import { setBreadcrumb } from "./helpers";
import { useAuth } from "@/context/AuthContext";
import { FEATURE_FLAG_COVER_CARD_NEW_VISUAL } from "@/pages/Home";
import { ExternalHtml } from "@/components/ExternalHtml";
import {
	useWebinarSubscription,
	useCourseSubscription,
} from "@/hooks/useWorkshopSubscription";
import { useQueryClient } from "@tanstack/react-query";

const openWorkshopSite = async (preparedUrl: string): Promise<void> => {
	await Browser.open({ url: preparedUrl });
};

// First, define the types
type DrawerState = {
	open: boolean;
	step: "go_to_login" | "go_to_website";
};

type DrawerAction =
	| { type: "OPEN_DRAWER" }
	| { type: "CLOSE_DRAWER" }
	| { type: "SET_STEP"; payload: "go_to_login" | "go_to_website" };

// Define the initial state
const initialState: DrawerState = {
	open: false,
	step: "go_to_login",
};

const drawerReducer = (
	state: DrawerState,
	action: DrawerAction
): DrawerState => {
	switch (action.type) {
		case "OPEN_DRAWER":
			return {
				open: true,
				step: "go_to_login",
			};
		case "CLOSE_DRAWER":
			return {
				...state,
				open: false,
			};
		case "SET_STEP":
			return {
				...state,
				step: action.payload,
			};
		default:
			return state;
	}
};

export const WorkshopContentAuth = (): JSX.Element => {
	const { type } = useParams({
		from: "/(app)/_app/workshops_/$type/$slugId/auth",
	});
	const router = useRouter();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [addToCalendarStatus, setAddToCalendarStatus] = useState<
		null | "loading" | "success" | "accessDenied" | "error"
	>(null);
	const { isAuthenticated } = useAuth();

	const webinarSubscription = useWebinarSubscription();
	const courseSubscription = useCourseSubscription();

	const { studyWorkshopDetails } = useLoaderData({
		from: "/(app)/_app/workshops_/$type/$slugId/auth",
	});

	const [state, dispatch] = useReducer(drawerReducer, initialState);
	// const search = useSearch({
	// 	from: "/(app)/_app/workshops_/$type/$slugId/auth",
	// });

	const showToast = async (message: string): Promise<void> => {
		console.log("showToast", message);
		await Toast.show({
			// text: "Zapisałeś się na wydarzenie",
			text: message,
			duration: "long",
			position: "top",
		});
	};

	if (studyWorkshopDetails instanceof Error) {
		return <div>Error: {studyWorkshopDetails.message}</div>;
	}

	if (studyWorkshopDetails === undefined) {
		return (
			<>
				<Skeleton className="h-[40px] mb-4" />
				<Skeleton className="h-[175px] mb-4" />
				<Skeleton className="h-[36px] mb-4" />
				<Skeleton className="h-[48px] mb-4" />
				<Skeleton className="h-[20px] mb-2" />
				<Skeleton className="h-[20px] mb-2" />
				<Skeleton className="h-[20px] mb-2" />
				<Skeleton className="h-[20px] mb-2" />
				<Skeleton className="h-[20px] mb-2" />
				<Skeleton className="h-[20px] mb-2" />
				<Skeleton className="h-[20px] mb-2" />
			</>
		);
	}

	const workshopActionAddWebinarToCalendar = (): void => {
		setAddToCalendarStatus("loading");
		webinarSubscription.mutate(studyWorkshopDetails.course.slug, {
			onSuccess: () => {
				// Invalidate profile queries to refresh user's subscriptions
				void queryClient.invalidateQueries({ queryKey: ["profile"] });
				void navigate({
					to: "/study/$studyId",
					params: { studyId: studyWorkshopDetails?.course.slug ?? "" },
					search: {
						status:
							(studyWorkshopDetails?.course.type_field as StudyStatus) ??
							"Webinar-online",
					},
				});
				void showToast("Przypomnienie o wydarzeniu zostało wysłane na e-mail");
				setAddToCalendarStatus("success");
			},
			onError: (error) => {
				// Check if it's a 403 error with "User cannot be added" message
				if (axios.isAxiosError(error) && error.response?.status === 403) {
					setAddToCalendarStatus("accessDenied");
					void showToast("Nie masz dostępu do tego webinaru");
				} else {
					setAddToCalendarStatus("error");
					void showToast("Akcja nie powiodła się. Spróbuj ponownie później.");
				}
			},
		});
	};

	const workshopActionAddCourseToCalendar = (): void => {
		console.log("workshopActionAddCourseToCalendar", studyWorkshopDetails.course.slug);
		setAddToCalendarStatus("loading");
		courseSubscription.mutate(studyWorkshopDetails.course.slug, {
			onSuccess: () => {
				// Invalidate profile queries to refresh user's subscriptions
				void queryClient.invalidateQueries({ queryKey: ["profile"] });
				void navigate({
					to: "/study/$studyId",
					params: { studyId: studyWorkshopDetails?.course.slug ?? "" },
					search: {
						status:
							(studyWorkshopDetails?.course.type_field as StudyStatus) ??
							"Webinar-online",
					},
				});
				void showToast("Przypomnienie o wydarzeniu zostało wysłane na e-mail");
				setAddToCalendarStatus("success");
			},
			onError: (error) => {
				// Check if it's a 403 error with "User cannot be added" message
				if (axios.isAxiosError(error) && error.response?.status === 403) {
					setAddToCalendarStatus("accessDenied");
					void showToast("Nie masz dostępu do tego szkolenia");
				} else {
					setAddToCalendarStatus("error");
					void showToast("Akcja nie powiodła się. Spróbuj ponownie później.");
				}
			},
		});
	};

	const workshopActionRedirectionToDetails = async (): Promise<void> => {
		if (isAuthenticated) {
			await navigate({
				to: "/study/$studyId",
				params: { studyId: studyWorkshopDetails?.course.slug ?? "" },
				search: {
					status:
						(studyWorkshopDetails?.course.type_field as StudyStatus) ??
						"Kurs-online",
				},
			});
		} else {
			dispatch({ type: "OPEN_DRAWER" });
		}
	};

	const isUpcoming = studyWorkshopDetails.course.type_field
		.toLowerCase()
		.includes("online");
	const isWebinar = studyWorkshopDetails.course.type_field
		.toLowerCase()
		.includes("webinar");
	const isCourse =
		studyWorkshopDetails.course.type_field
			.toLowerCase()
			.includes("szkolenie") ||
		studyWorkshopDetails.course.type_field.toLowerCase().includes("kurs");

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			{studyWorkshopDetails && (
				<>
					<div className="flex text-fontDarkGray pb-4 line-clamp-1">
						<button
							className="whitespace-nowrap flex-shrink-0"
							onClick={() => {
								router.history.back();
							}}
						>
							{setBreadcrumb(
								studyWorkshopDetails?.course?.type_field as StudyStatus
							)}
						</button>
						<span className="px-4">&#8226;</span>
						<span className="truncate min-w-0">
							{studyWorkshopDetails.course.name}
						</span>
					</div>
					{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? (
						<CoverCardNewVisual
							key={`CoverCardNewVisual-workshop ${studyWorkshopDetails.course.id}`}
							authors={studyWorkshopDetails.course.authors_users_data}
							categories={[studyWorkshopDetails.course.tags]}
							coverId={Number(studyWorkshopDetails.course.id)}
							title={studyWorkshopDetails.course.name}
							coverDate={chooseCoverDate(
								studyWorkshopDetails.course.starts_at,
								studyWorkshopDetails.course["legal-status"]
							)}
							slug={studyWorkshopDetails.course.slug}
						/>
					) : (
						<CoverCardMVP
							key={`CoverCardMVP-workshop ${studyWorkshopDetails.course.id}`}
							authors={studyWorkshopDetails.course.authors_users_data}
							categories={[studyWorkshopDetails.course.tags]}
							coverId={Number(studyWorkshopDetails.course.id)}
							title={studyWorkshopDetails.course.name}
							coverDate={chooseCoverDate(
								studyWorkshopDetails.course.starts_at,
								studyWorkshopDetails.course["legal-status"]
							)}
							slug={studyWorkshopDetails.course.slug}
						/>
					)}
					<Drawer
						open={state.open}
						onOpenChange={(open) => {
							dispatch({ type: open ? "OPEN_DRAWER" : "CLOSE_DRAWER" });
						}}
					>
						<DrawerContentWithoutCancelButton>
							<div className="m-10 text-center">
								{state.step === "go_to_login" && (
									<>
										<img
											alt="website ilustration"
											className="mx-auto w-[130px] h-auto"
											src={websiteIllustration}
										/>
										<h2 className="text-xl text-[#637381] pb-4 text-center my-6">
											Czy posiadasz aktywny abonament LTCA lub masz wykupiony
											dostęp do {type === "webinar" ? "webinaru" : "webinaru"}?
										</h2>
										<div className="flex justify-around">
											<Button
												className="w-[150px] h-[50px] bg-[#919EAB14] text-black"
												// variant="secondary"
												onClick={() => {
													dispatch({
														type: "SET_STEP",
														payload: "go_to_website",
													});
												}}
											>
												NIE
											</Button>
											<Link to="/login">
												<Button className="w-[150px] h-[50px]">TAK</Button>
											</Link>
										</div>
									</>
								)}
								{state.step === "go_to_website" && (
									<>
										<h3 className="text-lg font-bold text-center mb-10">
											Wybierz sposób dostępu
										</h3>
										<p className="text-[#637381] mb-10">
											Możesz kupić pojedynczy dostęp lub wykupić abonament,
											który daje Ci dostęp do wszystkich wydarzeń na platformie.
										</p>
										<p className="text-[#637381] text-center mb-2">
											Dostęp do wszystkich wydarzeń na platformie
										</p>
										<Button
											className="w-full max-w-[600px]"
											onClick={async () => {
												await openWorkshopSite(
													"https://akademialtca.pl/abonament/"
												);
											}}
										>
											Wybierz Abonament
										</Button>
										<p className="text-[#637381] text-center mt-6 mb-2">
											Dostęp do wydarzenia na zawsze
										</p>
										<Button
											className="w-full max-w-[600px] bg-[#919EAB14] text-black"
											onClick={async () => {
												await openWorkshopSite(
													`https://akademialtca.pl/${studyWorkshopDetails.course.slug}`
												);
											}}
										>
											Kup dostęp
										</Button>
									</>
								)}
							</div>
						</DrawerContentWithoutCancelButton>
					</Drawer>
					{isUpcoming && isCourse && addToCalendarStatus !== "accessDenied" && (
						<Button
							className="inline-block w-full  my-4"
							onClick={workshopActionAddCourseToCalendar}
						>
							{addToCalendarStatus === "loading" && (
								<LoaderSpinner borderColor="white" className="mr-3" size={6} />
							)}
							Zapisz się
						</Button>
					)}
					{isUpcoming && isCourse && addToCalendarStatus === "accessDenied" && (
						<div className="text-center text-sm my-5 text-rose-500">
							Nie masz dostępu do tego szkolenia
						</div>
					)}
					{!isUpcoming && isCourse && (
						<Button
							className="inline-block w-full  my-4"
							onClick={workshopActionRedirectionToDetails}
						>
							{studyWorkshopDetails.userOwnsProduct
								? "Kontynuuj"
								: "Rozpocznij"}
						</Button>
					)}
					{isUpcoming && isWebinar && addToCalendarStatus !== "accessDenied" && (
						<Button
							className="inline-block w-full  my-4"
							onClick={workshopActionAddWebinarToCalendar}
						>
							{addToCalendarStatus === "loading" && (
								<LoaderSpinner borderColor="white" className="mr-3" size={6} />
							)}
							Zapisz się
						</Button>
					)}
					{isUpcoming && isWebinar && addToCalendarStatus === "accessDenied" && (
						<div className="text-center text-sm my-5 text-rose-500">
							Nie masz dostępu do tego webinaru
						</div>
					)}
					{!isUpcoming && isWebinar && (
						<Button
							className="inline-block w-full  my-4"
							onClick={workshopActionRedirectionToDetails}
						>
							{studyWorkshopDetails.userOwnsProduct
								? "Kontynuuj"
								: "Rozpocznij"}
						</Button>
					)}
					<h1 className="text-2xl font-bold pb-4 pr-7">
						{studyWorkshopDetails.course.name}
					</h1>
					{studyWorkshopDetails.course.content && (
						<ExternalHtml
							html={studyWorkshopDetails.course.content}
							className="html-content text pr-7"
						/>
					)}
					<h5 className="text-sm font-medium pb-3">
						{studyWorkshopDetails.course.authors_users_data.length >= 2
							? "Wykładowcy:"
							: "Wykładowca:"}
					</h5>
					{/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
					<AuthorsSimplyList
						authors={studyWorkshopDetails.course.authors_users_data}
					/>
				</>
			)}
		</main>
	);
};
